/**
 * Character-level diff for KaTeX-rendered math formulas.
 *
 * Renders both formulas to KaTeX HTML, then matches text nodes between the
 * two `katex-html` spans using their ancestry-class signatures via LCS.
 *
 * - Matched text nodes with different content → character-level `<ins>`/`<del>`
 * - Text nodes only in the current formula → parent element wrapped in `<ins>`
 * - Text nodes only in the previous formula → parent element cloned, wrapped
 *   in `<del>`, and injected at the appropriate position in the current DOM
 *
 * Returns `null` when the formulas are too structurally different (less than
 * half of text nodes matched), signaling the caller to fall back to full
 * `<del>old</del><ins>new</ins>` replacement.
 */
import * as cheerio from 'cheerio';
import type { AnyNode, Element, Text } from 'domhandler';
import render from 'dom-serializer';

import { renderMathFormulaCached } from '../parser/parser';
import { diffSequence } from '../core/matcher';
import { escapeHtml } from './html';

// ---------------------------------------------------------------------------
// Text-node collection
// ---------------------------------------------------------------------------

/** Ordered text nodes inside the `katex-html` span (skips `katex-mathml`). */
const collectTextNodes = (node: AnyNode): Text[] => {
  const result: Text[] = [];

  const walk = (n: AnyNode): void => {
    if (n.type === 'text') {
      const t = n as Text;
      if (t.data) result.push(t);
      return;
    }
    if (n.type === 'tag') {
      const el = n as Element;
      // Skip the MathML accessibility span - it duplicates all formula text.
      if (el.attribs?.class?.includes('katex-mathml')) return;
      for (const child of el.children) walk(child);
    }
  };

  walk(node);
  return result;
};

// ---------------------------------------------------------------------------
// Leaf-slot signature
// ---------------------------------------------------------------------------

/**
 * Ancestry class path from a text node up to (but excluding) the root.
 * Two text nodes in different structural positions (e.g. base vs superscript)
 * will produce different signatures even when they carry the same character.
 */
const leafSignature = (textNode: Text, root: Element): string => {
  const parts: string[] = [];
  let current: AnyNode | null = textNode.parentNode as AnyNode | null;

  while (current && current !== root) {
    if (current.type === 'tag') {
      const el = current as Element;
      parts.push(el.attribs?.class || el.tagName);
    }
    current = 'parentNode' in current ? (current.parentNode as AnyNode | null) : null;
  }

  return parts.reverse().join('/');
};

// ---------------------------------------------------------------------------
// Character-level text diff
// ---------------------------------------------------------------------------

const diffTextContent = (prevText: string, currText: string): string => {
  if (prevText === currText) return escapeHtml(currText);

  const prevChars = [...prevText]; // spread handles multi-byte correctly
  const currChars = [...currText];
  const ops = diffSequence(prevChars, currChars, (a, b) => a === b);

  const parts: string[] = [];
  for (const op of ops) {
    if (op.type === 'equal') {
      parts.push(escapeHtml(op.currItem));
    } else if (op.type === 'insert') {
      parts.push(`<ins>${escapeHtml(op.currItem)}</ins>`);
    } else {
      parts.push(`<del>${escapeHtml(op.prevItem)}</del>`);
    }
  }
  return parts.join('');
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Attempts a character-level diff of two KaTeX-rendered math formulas.
 *
 * @returns Annotated KaTeX HTML with `<ins>`/`<del>` markers on changed
 *          characters, or `null` when the rendered structures differ too
 *          much and the caller should fall back to full replacement.
 */
export const diffRenderedMath = (
  prevFormula: string,
  currFormula: string,
  mathCache?: Map<string, string>
): string | null => {
  if (!prevFormula || !currFormula) return null;
  if (prevFormula === currFormula) return null;

  const prevHtml = renderMathFormulaCached(prevFormula, mathCache);
  const currHtml = renderMathFormulaCached(currFormula, mathCache);

  // If either render failed (returned raw LaTeX fallback), bail out.
  if (!prevHtml.includes('katex') || !currHtml.includes('katex')) return null;

  const $prev = cheerio.load(prevHtml);
  const $curr = cheerio.load(currHtml);

  const prevKatexHtml = $prev('.katex-html').get(0) as Element | undefined;
  const currKatexHtml = $curr('.katex-html').get(0) as Element | undefined;
  if (!prevKatexHtml || !currKatexHtml) return null;

  const prevTextNodes = collectTextNodes(prevKatexHtml);
  const currTextNodes = collectTextNodes(currKatexHtml);
  if (prevTextNodes.length === 0 && currTextNodes.length === 0) return null;

  // Build signatures for LCS matching.
  const prevSigs = prevTextNodes.map((n) => leafSignature(n, prevKatexHtml));
  const currSigs = currTextNodes.map((n) => leafSignature(n, currKatexHtml));

  const ops = diffSequence(prevSigs, currSigs, (a, b) => a === b);

  // Quality gate: if fewer than half the operations are matches, the formulas
  // are too structurally different for meaningful inline annotation.
  const equalCount = ops.filter((op) => op.type === 'equal').length;
  const totalLeaves = Math.max(prevTextNodes.length, currTextNodes.length);
  if (totalLeaves > 0 && equalCount / totalLeaves < 0.5) return null;

  // --- Apply annotations to the current DOM ---

  // Tracks the DOM node after which subsequent <del> elements should be
  // inserted.  Updated for every processed operation so that consecutive
  // deletions appear in the correct order.
  let insertionAnchor: AnyNode | null = null;
  let hasDiff = false;

  for (const op of ops) {
    if (op.type === 'equal') {
      const prevText = prevTextNodes[op.prevIndex!].data;
      const currText = currTextNodes[op.currIndex!].data;
      const currTextNode = currTextNodes[op.currIndex!];
      insertionAnchor = currTextNode.parentNode as AnyNode;

      if (prevText !== currText) {
        hasDiff = true;
        $curr(currTextNode).replaceWith(diffTextContent(prevText, currText));
      }
    } else if (op.type === 'insert') {
      hasDiff = true;
      const currTextNode = currTextNodes[op.currIndex!];
      const currLeaf = currTextNode.parentNode as Element;
      $curr(currLeaf).wrap('<ins></ins>');
      // After wrapping, the <ins> is the new parent - anchor on it.
      insertionAnchor = currLeaf.parentNode as AnyNode;
    } else {
      // delete - clone the leaf element from the previous DOM and inject
      // it wrapped in <del> after the current insertion anchor.
      hasDiff = true;
      const prevLeaf = prevTextNodes[op.prevIndex!].parentNode as Element;
      const clonedHtml = `<del>${render(prevLeaf)}</del>`;

      if (insertionAnchor) {
        $curr(insertionAnchor).after(clonedHtml);
        // Advance anchor to the newly inserted <del> so consecutive
        // deletions appear in order.
        const next = $curr(insertionAnchor).next().get(0);
        if (next) insertionAnchor = next as AnyNode;
      } else {
        // No anchor yet - prepend to the first .base span.
        const $firstBase = $curr('.katex-html .base').first();
        if ($firstBase.length) {
          $firstBase.prepend(clonedHtml);
          // Anchor on the prepended element for subsequent deletions.
          insertionAnchor = $firstBase.children().first().get(0) as AnyNode;
        }
      }
    }
  }

  if (!hasDiff) return null;

  // Return the full <span class="katex"> element.
  const $katex = $curr('.katex').first();
  return $katex.length ? $curr.html($katex) : null;
};
